import styled from 'styled-components'

import { Span } from '../typography/Body'
import { H3 } from '../typography/Heading'

import type { ComponentPropsWithoutRef } from 'react'

/* ------------------------------------------------------------------------------------------------- *
 * InfoPanel
 * ------------------------------------------------------------------------------------------------- */

type InfoPanelRootProps = ComponentPropsWithoutRef<'div'>

/** A scrollable panel of titled paragraphs, e.g. the details behind a "more information" link. */
const InfoPanelRoot = ({ children, ...props }: InfoPanelRootProps) => <StRoot {...props}>{children}</StRoot>

/* ------------------------------------------------------------------------------------------------- *
 * InfoPanel.Title
 * ------------------------------------------------------------------------------------------------- */

type InfoPanelTitleProps = ComponentPropsWithoutRef<typeof H3>

const InfoPanelTitle = ({ children, ...props }: InfoPanelTitleProps) => (
  <H3 noMargin {...props}>
    {children}
  </H3>
)

/* ------------------------------------------------------------------------------------------------- *
 * InfoPanel.Heading
 * ------------------------------------------------------------------------------------------------- */

type InfoPanelHeadingProps = ComponentPropsWithoutRef<typeof Span>

const InfoPanelHeading = ({ children, ...props }: InfoPanelHeadingProps) => (
  <StHeading type='BodyMediumBold' {...props}>
    {children}
  </StHeading>
)

/* ------------------------------------------------------------------------------------------------- *
 * InfoPanel.Body
 * ------------------------------------------------------------------------------------------------- */

type InfoPanelBodyProps = ComponentPropsWithoutRef<typeof Span>

const InfoPanelBody = ({ children, ...props }: InfoPanelBodyProps) => (
  <StBody type='BodyMediumRegular' {...props}>
    {children}
  </StBody>
)

/* ------------------------------------------------------------------------------------------------- *
 * Compound export
 * ------------------------------------------------------------------------------------------------- */

const InfoPanel = Object.assign(InfoPanelRoot, {
  Title: InfoPanelTitle,
  Heading: InfoPanelHeading,
  Body: InfoPanelBody,
})

export { InfoPanel }

/* ------------------------------------------------------------------------------------------------- *
 * Styled
 * ------------------------------------------------------------------------------------------------- */

const StRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  gap: ${({ theme }) => theme.UI.SpacingPx.Space6};
`

/* Block so a heading and its body stack without layout at the call site. */
const StHeading = styled(Span)`
  display: block;
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
`

const StBody = styled(Span)`
  display: block;
  color: ${({ theme }) => theme.Colors.Neutrals.Darker};
`
